(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-products-slider-inline]';
  const SLIDE_SELECTOR = '.products-slider-inline__slide';
  const STORAGE_KEY = 'drhoelter_recently_viewed';

  if (window.DrhProductsSliderInline?.initScope) {
    window.DrhProductsSliderInline.initScope(document);
    return;
  }

  function rootsInScope(scope) {
    if (!scope) return [];
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(root => roots.push(root));
    return roots;
  }

  function addListener(cleanups, target, type, listener, options) {
    target.addEventListener(type, listener, options);
    cleanups.push(() => target.removeEventListener(type, listener, options));
  }

  function parseNumber(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function routesRoot(root) {
    const configured = root.dataset.routesRoot || window.Shopify?.routes?.root || '/';
    return configured.endsWith('/') ? configured : `${configured}/`;
  }

  function isAbortError(error) {
    return error?.name === 'AbortError';
  }

  class InlineCarouselController {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-inline-track]');
      this.previousButton = root.querySelector('[data-slider-inline-prev]');
      this.nextButton = root.querySelector('[data-slider-inline-next]');
      this.trackWrapper = root.querySelector('.products-slider-inline__track-wrapper');
      this.slides = this.track ? Array.from(this.track.querySelectorAll(SLIDE_SELECTOR)) : [];
      this.cardsDesktop = parseNumber(root.dataset.cardsDesktop, 4);
      this.cardsMobile = parseNumber(root.dataset.cardsMobile, 2);
      this.currentIndex = 0;
      this.wheelDelta = 0;
      this.wheelSnapTimer = null;
      this.scrollEndTimer = null;
      this.resizeTimer = null;
      this.focusSyncFrame = null;
      this.cleanups = [];
      this.loading = root.getAttribute('aria-busy') === 'true';
      this.destroyed = false;
      this.desktopMode = this.isDesktop();
      this.motionQuery = typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
      this.init();
    }

    isDesktop() {
      return window.innerWidth >= 768;
    }

    visibleCards() {
      if (this.isDesktop()) return this.cardsDesktop;
      return this.root.closest('.article-content-section') ? 1 : this.cardsMobile;
    }

    maxIndex() {
      return Math.max(0, this.slides.length - this.visibleCards());
    }

    needsSlider() {
      return this.slides.length > this.visibleCards();
    }

    slideUnit() {
      if (!this.slides[0]) return 0;
      return this.slides[0].offsetWidth + (this.isDesktop() ? 16 : 12);
    }

    syncImageSlotSizes() {
      const slotWidth = Math.ceil(this.slides[0]?.getBoundingClientRect().width || this.slides[0]?.offsetWidth || 0);
      if (slotWidth <= 0) return;
      this.slides.forEach(slide => {
        slide.querySelectorAll('img[srcset]').forEach(image => {
          image.setAttribute('sizes', `${slotWidth}px`);
        });
      });
    }

    setLoading(loading) {
      this.loading = loading;
      this.updateControls();
    }

    syncSlideAccessibility() {
      const desktop = this.isDesktop();
      const visibleEnd = this.currentIndex + this.visibleCards();
      const activeElement = document.activeElement;

      this.slides.forEach((slide, index) => {
        const outsideWindow = desktop && (index < this.currentIndex || index >= visibleEnd);
        const containsFocus = activeElement && slide.contains(activeElement);
        if (outsideWindow && !containsFocus) {
          slide.setAttribute('inert', '');
          slide.setAttribute('aria-hidden', 'true');
        } else {
          slide.removeAttribute('inert');
          if (desktop) slide.setAttribute('aria-hidden', 'false');
          else slide.removeAttribute('aria-hidden');
        }
      });
    }

    updateControls() {
      const needed = this.needsSlider();
      const maximum = this.maxIndex();

      if (this.previousButton) {
        this.previousButton.hidden = !needed;
        this.previousButton.disabled = !needed || this.loading || this.currentIndex <= 0;
      }
      if (this.nextButton) {
        this.nextButton.hidden = !needed;
        this.nextButton.disabled = !needed || this.loading || this.currentIndex >= maximum;
      }

      if (this.track) this.track.style.overflowX = needed ? '' : 'hidden';
      if (!needed) {
        this.currentIndex = 0;
        if (this.track) {
          this.track.style.transform = '';
          this.track.style.transition = '';
        }
      }
      this.syncSlideAccessibility();
    }

    updatePosition(animate = true) {
      if (!this.track || this.slides.length === 0) return;
      if (!this.needsSlider()) {
        this.updateControls();
        return;
      }

      const offset = this.currentIndex * this.slideUnit();
      const shouldAnimate = animate && !(this.motionQuery?.matches);
      if (this.isDesktop()) {
        this.track.style.transition = shouldAnimate ? 'transform 0.4s ease' : 'none';
        this.track.style.transform = `translateX(-${offset}px)`;
      } else if (typeof this.track.scrollTo === 'function') {
        this.track.scrollTo({ left: offset, behavior: shouldAnimate ? 'smooth' : 'instant' });
      }
      this.updateControls();
    }

    goNext() {
      if (!this.needsSlider() || this.currentIndex >= this.maxIndex()) return;
      this.currentIndex += 1;
      this.updatePosition();
    }

    goPrevious() {
      if (!this.needsSlider() || this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      this.updatePosition();
    }

    init() {
      if (!this.track || this.slides.length === 0) {
        this.updateControls();
        return;
      }

      if (this.previousButton) {
        addListener(this.cleanups, this.previousButton, 'click', () => this.goPrevious());
      }
      if (this.nextButton) {
        addListener(this.cleanups, this.nextButton, 'click', () => this.goNext());
      }

      if (this.motionQuery) {
        addListener(this.cleanups, this.motionQuery, 'change', () => this.updatePosition(false));
      }

      if (this.trackWrapper) {
        addListener(this.cleanups, this.trackWrapper, 'wheel', event => {
          if (!this.needsSlider() || !this.isDesktop()) return;
          const horizontal = Math.abs(event.deltaX);
          if (horizontal < 5 || horizontal < Math.abs(event.deltaY)) return;

          event.preventDefault();
          const unit = this.slideUnit();
          const maximum = this.maxIndex() * unit;
          const current = this.currentIndex * unit;
          this.wheelDelta += event.deltaX;
          const provisional = Math.max(0, Math.min(current + this.wheelDelta, maximum));
          this.track.style.transition = 'none';
          this.track.style.transform = `translateX(-${provisional}px)`;

          window.clearTimeout(this.wheelSnapTimer);
          this.wheelSnapTimer = window.setTimeout(() => {
            this.currentIndex = Math.max(0, Math.min(Math.round(provisional / unit), this.maxIndex()));
            this.wheelDelta = 0;
            this.updatePosition();
          }, 120);
        }, { passive: false });
      }

      addListener(this.cleanups, this.track, 'scroll', () => {
        if (this.isDesktop()) return;
        const unit = this.slideUnit();
        if (unit > 0) {
          this.currentIndex = Math.max(0, Math.min(Math.round(this.track.scrollLeft / unit), this.maxIndex()));
        }
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => this.updateControls(), 120);
      }, { passive: true });

      addListener(this.cleanups, this.root, 'focusout', () => {
        window.cancelAnimationFrame(this.focusSyncFrame);
        this.focusSyncFrame = window.requestAnimationFrame(() => {
          this.focusSyncFrame = null;
          if (!this.destroyed) this.syncSlideAccessibility();
        });
      });

      addListener(this.cleanups, window, 'resize', () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          const desktop = this.isDesktop();
          const crossedBreakpoint = desktop !== this.desktopMode;
          this.desktopMode = desktop;
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex());
          this.wheelDelta = 0;
          if (crossedBreakpoint) {
            this.currentIndex = 0;
            this.track.style.transform = '';
            this.track.style.transition = '';
            this.track.scrollLeft = 0;
          }
          this.syncImageSlotSizes();
          this.updateControls();
          this.updatePosition(false);
        }, 100);
      });

      this.syncImageSlotSizes();
      this.updateControls();
      this.updatePosition(false);
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      window.clearTimeout(this.wheelSnapTimer);
      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.resizeTimer);
      window.cancelAnimationFrame(this.focusSyncFrame);
      this.cleanups.splice(0).forEach(cleanup => cleanup());
      this.slides.forEach(slide => {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      });
      if (this.track) {
        this.track.style.transform = '';
        this.track.style.transition = '';
        this.track.style.overflowX = '';
      }
      [this.previousButton, this.nextButton].forEach(button => {
        if (!button) return;
        button.hidden = false;
        button.disabled = false;
      });
    }
  }

  class InlineProductsSliderController {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-slider-inline-track]');
      this.status = root.querySelector('[data-slider-inline-status]');
      this.editorPlaceholder = root.querySelector('[data-slider-inline-editor-placeholder]');
      this.carousel = null;
      this.loaderRequest = null;
      this.loaderGeneration = 0;
      this.cleanups = [];
      this.destroyed = false;

      root.__productsSliderInlineController = this;
      root.setAttribute('data-products-slider-inline-initialized', 'true');

      if (root.hasAttribute('data-slider-inline-recommendations')) this.loadRecommendations();
      else if (root.hasAttribute('data-slider-inline-recently-viewed')) this.loadRecentlyViewed();
      else this.finishStatic();
    }

    text(name, fallback = '') {
      return this.root.dataset[name] || fallback;
    }

    setStatus(message, state = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
      if (state) this.status.dataset.state = state;
      else delete this.status.dataset.state;
    }

    setBusy(busy) {
      this.root.setAttribute('aria-busy', String(busy));
      this.track?.toggleAttribute('inert', busy);
      this.carousel?.setLoading(busy);
    }

    setControlsUnavailable(unavailable) {
      [
        this.root.querySelector('[data-slider-inline-prev]'),
        this.root.querySelector('[data-slider-inline-next]'),
      ].forEach(button => {
        if (!button) return;
        button.hidden = unavailable;
        button.disabled = unavailable;
      });
    }

    beginLoader() {
      this.setControlsUnavailable(true);
      this.setBusy(true);
      this.setStatus(this.text('loadingText'), 'loading');
    }

    finishStatic() {
      this.revealContent();
      this.applyCardConfig();
      this.rebuildCarousel();
      this.root.classList.add('is-loaded');
      this.root.removeAttribute('aria-hidden');
      this.setBusy(false);
      this.setStatus('');
    }

    finishLoader() {
      this.revealContent();
      this.applyCardConfig();
      this.rebuildCarousel();
      this.root.classList.add('is-loaded');
      this.root.removeAttribute('aria-hidden');
      this.setBusy(false);
      this.setStatus('');
    }

    rebuildCarousel() {
      this.carousel?.destroy();
      this.carousel = new InlineCarouselController(this.root);
    }

    revealContent() {
      if (this.track) this.track.hidden = false;
      if (this.editorPlaceholder) {
        this.editorPlaceholder.hidden = true;
        this.editorPlaceholder.textContent = '';
      }
      this.root.classList.remove('products-slider-inline--placeholder');
    }

    loaderController() {
      this.loaderRequest?.abort();
      this.loaderRequest = new window.AbortController();
      return { request: this.loaderRequest, generation: ++this.loaderGeneration };
    }

    loaderIsCurrent(generation) {
      return !this.destroyed && this.root.isConnected && generation === this.loaderGeneration;
    }

    rescueFocusBeforeTrackReplace() {
      if (!this.track?.contains(document.activeElement)) return;
      const hadTabindex = this.root.hasAttribute('tabindex');
      if (!hadTabindex) this.root.setAttribute('tabindex', '-1');
      try {
        this.root.focus({ preventScroll: true });
      } catch {
        this.root.focus();
      }
      if (!hadTabindex) {
        const removeTabindex = () => this.root.removeAttribute('tabindex');
        this.root.addEventListener('blur', removeTabindex, { once: true });
        this.cleanups.push(() => this.root.removeEventListener('blur', removeTabindex));
      }
    }

    commitTrack(html) {
      if (!this.track) return;
      this.rescueFocusBeforeTrackReplace();
      this.track.innerHTML = html;
    }

    removeRoot() {
      const root = this.root;
      this.destroy();
      root.remove();
    }

    finishUnavailable(state) {
      if (this.root.dataset.designMode !== 'true') {
        this.removeRoot();
        return;
      }

      const message = this.text(state === 'error' ? 'errorText' : 'emptyText');
      this.carousel?.destroy();
      this.carousel = null;
      if (this.track) {
        this.rescueFocusBeforeTrackReplace();
        this.track.replaceChildren();
        this.track.hidden = true;
      }
      if (this.editorPlaceholder) {
        this.editorPlaceholder.textContent = message;
        this.editorPlaceholder.hidden = false;
      }
      this.root.classList.add('is-loaded', 'products-slider-inline--placeholder');
      this.root.removeAttribute('aria-hidden');
      this.setBusy(false);
      this.setControlsUnavailable(true);
      this.setStatus(message, state);
    }

    parseRecommendationTrack(html) {
      const parsed = new window.DOMParser().parseFromString(html, 'text/html');
      const blockId = this.root.dataset.blockId;
      const candidates = Array.from(parsed.querySelectorAll(ROOT_SELECTOR));
      const matches = candidates.filter(candidate => candidate.dataset.blockId === blockId);
      const responseRoot = matches.length === 1
        ? matches[0]
        : (!blockId && candidates.length === 1 ? candidates[0] : null);
      const nextTrack = responseRoot?.querySelector('[data-slider-inline-track]');
      if (!nextTrack) throw new Error('Inline products slider response did not contain the requested block track');
      return nextTrack.innerHTML;
    }

    async loadRecommendations() {
      const productId = this.root.dataset.productId;
      const sectionId = this.root.dataset.sectionId;
      if (!productId || !sectionId || !this.track) {
        console.warn('Inline products slider recommendations: Missing product, section, or track');
        this.finishUnavailable('error');
        return;
      }

      const { request, generation } = this.loaderController();
      this.beginLoader();
      const endpoint = this.root.dataset.recommendationsUrl || `${routesRoot(this.root)}recommendations/products`;
      try {
        const htmlResponse = await window.DrhRecommendations.request({
          endpoint, productId, sectionId, limit: this.root.dataset.maxProducts || 10,
          intent: this.root.dataset.intent || 'related', signal: request.signal,
        });
        const html = this.parseRecommendationTrack(htmlResponse);
        if (!this.loaderIsCurrent(generation)) return;
        const temporary = document.createElement('div');
        temporary.innerHTML = html;
        if (!temporary.querySelector(SLIDE_SELECTOR)) {
          this.finishUnavailable('empty');
          return;
        }
        this.commitTrack(html);
        this.finishLoader();
      } catch (error) {
        if (!this.loaderIsCurrent(generation) || isAbortError(error)) return;
        console.warn('Inline products slider: Could not load recommendations', error);
        this.finishUnavailable('error');
      }
    }

    readRecentlyViewed(maximum) {
      let stored;
      try {
        stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || [];
      } catch {
        stored = [];
      }
      if (!Array.isArray(stored)) return [];

      const currentProduct = this.root.dataset.currentProduct;
      const seen = new Set();
      return stored
        .map(item => typeof item === 'string' ? { handle: item } : item)
        .filter(item => {
          const handle = item?.handle;
          if (!handle || handle === currentProduct || seen.has(handle)) return false;
          seen.add(handle);
          return true;
        })
        .slice(0, maximum);
    }

    async loadRecentlyViewed() {
      if (!this.track) {
        this.finishUnavailable('error');
        return;
      }

      const { request, generation } = this.loaderController();
      this.beginLoader();
      const maximum = parseNumber(this.root.dataset.maxProducts, 6);
      const recentlyViewed = this.readRecentlyViewed(maximum);
      if (recentlyViewed.length === 0) {
        this.finishUnavailable('empty');
        return;
      }

      const ratingMap = {};
      recentlyViewed.forEach(item => {
        if (item.rating || item.ratingCount) {
          ratingMap[item.handle] = {
            rating: item.rating || 0,
            ratingCount: item.ratingCount || 0,
          };
        }
      });

      try {
        const products = await Promise.all(recentlyViewed.map(async item => {
          try {
            const response = await window.fetch(
              `${routesRoot(this.root)}products/${encodeURIComponent(item.handle)}.js`,
              { signal: request.signal }
            );
            return response.ok ? await response.json() : null;
          } catch (error) {
            if (isAbortError(error)) throw error;
            return null;
          }
        }));
        if (!this.loaderIsCurrent(generation)) return;
        const validProducts = products.filter(Boolean);
        if (validProducts.length === 0) {
          this.finishUnavailable('empty');
          return;
        }

        this.commitTrack(validProducts.map(product => this.productCardHtml(
          product,
          ratingMap[product.handle] || null
        )).join(''));
        this.finishLoader();
      } catch (error) {
        if (!this.loaderIsCurrent(generation) || isAbortError(error)) return;
        console.warn('Inline products slider: Could not load recently viewed products', error);
        this.finishUnavailable('error');
      }
    }

    applyCardConfig() {
      const block = this.root.closest('.article-content-section__block');
      const nextBlock = block?.nextElementSibling;
      const config = nextBlock?.querySelector('[data-card-config]');
      if (!config) return;

      const settings = config.dataset;
      this.root.querySelectorAll(SLIDE_SELECTOR).forEach(slide => {
        const rating = slide.querySelector('.product-card__rating');
        const vendor = slide.querySelector('.product-card__vendor');
        const price = slide.querySelector('.product-card__price');
        const comparePrice = slide.querySelector('.product-card__price-compare');
        const badges = slide.querySelectorAll('.product-card__badge');
        const hoverImage = slide.querySelector('.product-card__image--hover');
        const imageWrapper = slide.querySelector('.product-card__image-wrapper');

        if (rating) rating.hidden = settings.showRating === 'false';
        if (vendor) vendor.hidden = settings.showVendor === 'false';
        if (price) price.hidden = settings.showPrice === 'false';
        if (comparePrice) comparePrice.hidden = settings.showComparePrice === 'false';
        badges.forEach(badge => { badge.hidden = settings.showBadges === 'false'; });
        if (hoverImage) hoverImage.hidden = settings.showHoverImage === 'false';
        if (imageWrapper && settings.imageRatio) {
          imageWrapper.className = imageWrapper.className.replace(
            /product-card__image-wrapper--\w+/,
            `product-card__image-wrapper--${settings.imageRatio}`
          );
        }
      });
    }

    productCardHtml(product, storedRating) {
      const showRating = this.root.dataset.showRating !== 'false';
      const showVendor = this.root.dataset.showVendor !== 'false';
      const showPrice = this.root.dataset.showPrice !== 'false';
      const showQuickBuy = this.root.dataset.showQuickBuy !== 'false';
      const showSaleBadge = this.root.dataset.showSaleBadge === 'true';
      const imageRatio = ['portrait', 'landscape'].includes(this.root.dataset.imageRatio)
        ? this.root.dataset.imageRatio
        : 'square';
      const image = product.featured_image || product.images?.[0];
      const imageUrl = this.sizedImageUrl(image, '200x');
      const imageSrcset = this.imageCandidateWidths()
        .map(width => `${this.sizedImageUrl(image, `${width}x`)} ${width}w`)
        .join(', ');
      const imageSizes = this.imageSizes();
      const productUrl = this.productUrl(product);
      const comparePrice = product.compare_at_price > product.price
        ? this.formatMoney(product.compare_at_price)
        : '';
      const rating = showRating ? this.ratingHtml(storedRating) : '';
      const cardSchemeClass = this.text('cardSchemeClass');
      const ctaScheme = /^[1-5]$/.test(this.text('ctaColorScheme'))
        ? ` product-card__quick-buy--themed color-scheme-${this.text('ctaColorScheme')}`
        : '';
      const badge = showSaleBadge && comparePrice
        ? `<span class="product-card__badge product-card__badge--sale">${this.escapeHtml(this.text('saleText'))}</span>`
        : '';
      let quickBuy = '';
      if (showQuickBuy && product.available) {
        quickBuy = `<a href="${this.escapeHtml(productUrl)}" class="product-card__quick-buy${ctaScheme}">${this.escapeHtml(this.text('moreInfoText'))}</a>`;
      } else if (showQuickBuy && !product.available) {
        quickBuy = `<a href="${this.escapeHtml(productUrl)}" class="product-card__quick-buy product-card__quick-buy--view">${this.escapeHtml(this.text('viewProductText'))}</a>`;
      }

      return `
        <div class="products-slider-inline__slide${cardSchemeClass ? ` ${this.escapeHtml(cardSchemeClass)}` : ''}" role="listitem">
          <article class="product-card product-card--compact">
            <a href="${this.escapeHtml(productUrl)}" class="product-card__link">
              <div class="product-card__image-wrapper product-card__image-wrapper--${imageRatio}">
                ${imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" srcset="${this.escapeHtml(imageSrcset)}" sizes="${this.escapeHtml(imageSizes)}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="200" height="200" class="product-card__image">` : '<div class="product-card__image product-card__image--placeholder"></div>'}
                ${badge}
              </div>
              <div class="product-card__info">
                ${rating}
                ${showVendor && product.vendor ? `<p class="product-card__vendor">${this.escapeHtml(product.vendor)}</p>` : ''}
                <h3 class="product-card__title">${this.escapeHtml(product.title)}</h3>
                ${showPrice && product.price > 0 ? `<div class="product-card__price">${product.price_varies === true ? `<span class="product-card__price-from">${this.escapeHtml(this.text('fromText'))} </span>` : ''}<span class="product-card__price-amount">${this.formatMoney(product.price)}</span>${comparePrice ? `<span class="product-card__price-compare">${comparePrice}</span>` : ''}</div>` : ''}
              </div>
            </a>
            ${quickBuy}
          </article>
        </div>`;
    }

    ratingHtml(ratingData) {
      const rating = Number(ratingData?.rating || 0);
      const count = Number(ratingData?.ratingCount || 0);
      if (!(rating > 0) || !(count > 0)) return '';
      const fullStars = Math.floor(rating);
      const hasHalf = rating % 1 > 0;
      let stars = '';
      for (let index = 1; index <= 5; index += 1) {
        if (index <= fullStars) stars += this.starIcon('filled');
        else if (index === fullStars + 1 && hasHalf) stars += this.starIcon('half');
        else stars += this.starIcon('empty');
      }
      const label = `${Math.round(rating * 10) / 10}/5`;
      return `<div class="product-card__rating" aria-label="${this.escapeHtml(label)}"><div class="product-card__stars" aria-hidden="true">${stars}</div></div>`;
    }

    starIcon(kind) {
      const path = 'M8 0L10.3511 4.76393L15.6085 5.52786L11.8042 9.23607L12.7023 14.4721L8 12L3.29772 14.4721L4.19577 9.23607L0.391548 5.52786L5.64886 4.76393L8 0Z';
      if (kind === 'half') {
        return `<svg class="star-half" width="16" height="16" viewBox="-0.5 -0.5 17 15.5" fill="none" aria-hidden="true"><path d="${path}" fill="currentColor" fill-opacity="0.25" stroke="currentColor" stroke-width="1"></path><path d="M8 0L5.64886 4.76393L0.391548 5.52786L4.19577 9.23607L3.29772 14.4721L8 12V0Z" fill="currentColor"></path></svg>`;
      }
      const attributes = kind === 'filled'
        ? 'fill="currentColor"'
        : 'stroke="currentColor" stroke-width="1" fill="currentColor" fill-opacity="0.25"';
      return `<svg class="star-${kind}" width="16" height="16" viewBox="-0.5 -0.5 17 15.5" fill="none" aria-hidden="true"><path d="${path}" ${attributes}></path></svg>`;
    }

    formatMoney(cents) {
      const amount = Number(cents);
      if (!Number.isFinite(amount)) return '';
      if (typeof window.Shopify?.formatMoney === 'function') {
        return window.Shopify.formatMoney(amount, this.text('moneyFormat'));
      }
      const locale = this.text('locale') || document.documentElement.lang || undefined;
      const currency = window.Shopify?.currency?.active || this.text('currencyCode');
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount / 100);
      } catch {
        return (amount / 100).toFixed(2);
      }
    }

    productUrl(product) {
      const rootPath = routesRoot(this.root);
      const fallback = `${rootPath}products/${encodeURIComponent(product?.handle || '')}`;
      const source = product?.url || fallback;
      try {
        const url = new URL(source, window.location.origin);
        if (
          url.origin === window.location.origin
          && rootPath !== '/'
          && url.pathname.startsWith('/products/')
          && !url.pathname.startsWith(rootPath)
        ) {
          url.pathname = `${rootPath.slice(0, -1)}${url.pathname}`;
        }
        return url.origin === window.location.origin
          ? `${url.pathname}${url.search}${url.hash}`
          : url.href;
      } catch {
        return fallback;
      }
    }

    sizedImageUrl(source, size) {
      if (!source) return '';
      if (source.includes(`_${size}`)) return source;
      const match = source.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
      if (!match) return source;
      const base = source.slice(0, match.index).replace(/_\d+x(?:\d+)?(?:_crop_[^.]*)?$/i, '');
      return `${base}_${size}${match[0]}`;
    }

    imageSizes() {
      const mobileCards = Math.max(1, Math.min(2, parseNumber(this.root.dataset.cardsMobile, 2)));
      const desktopCards = Math.max(3, Math.min(6, parseNumber(this.root.dataset.cardsDesktop, 4)));
      const mobileSize = mobileCards === 1 ? 'calc(100vw - 64px)' : this.root.closest('.article-content-section') ? '70vw' : '44vw';
      const desktopSize = {
        3: 'calc(33.333vw - 26.667px)',
        4: 'calc(25vw - 24px)',
        5: 'calc(20vw - 22.4px)',
        6: 'calc(16.667vw - 21.333px)',
      }[desktopCards];
      return `(max-width: 767px) ${mobileSize}, ${desktopSize}`;
    }

    imageCandidateWidths() {
      const mobileCards = Math.max(1, Math.min(2, parseNumber(this.root.dataset.cardsMobile, 2)));
      const desktopCards = Math.max(3, Math.min(6, parseNumber(this.root.dataset.cardsDesktop, 4)));
      const maximum = mobileCards === 1 || this.root.closest('.article-content-section') ? 1600 : desktopCards === 3 ? 1200 : 800;
      return [200, 400, 600, 800, 1200, 1600].filter(width => width <= maximum);
    }

    escapeHtml(value) {
      const element = document.createElement('div');
      element.textContent = value || '';
      return element.innerHTML;
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.loaderGeneration += 1;
      this.loaderRequest?.abort();
      this.loaderRequest = null;
      this.carousel?.destroy();
      this.carousel = null;
      this.cleanups.splice(0).forEach(cleanup => cleanup());
      this.setBusy(false);
      this.setStatus('');
      this.root.removeAttribute('data-products-slider-inline-initialized');
      this.root.removeAttribute('tabindex');
      delete this.root.__productsSliderInlineController;
    }
  }

  const pendingLazyRoots = new WeakMap();

  const manager = {
    initScope(scope = document) {
      rootsInScope(scope).forEach(root => {
        if (root.__productsSliderInlineController || pendingLazyRoots.has(root)) return;
        const lazy = root.dataset.loadStrategy === 'lazy' && root.dataset.designMode !== 'true';
        if (lazy && typeof window.IntersectionObserver === 'function') {
          const observer = new window.IntersectionObserver(entries => {
            if (pendingLazyRoots.get(root) !== observer) return;
            if (!entries.some(entry => entry.target === root && entry.isIntersecting)) return;
            observer.disconnect();
            pendingLazyRoots.delete(root);
            if (root.isConnected && !root.__productsSliderInlineController) {
              new InlineProductsSliderController(root);
            }
          }, { rootMargin: '200px 0px', threshold: 0 });
          pendingLazyRoots.set(root, observer);
          observer.observe(root);
        } else {
          new InlineProductsSliderController(root);
        }
      });
    },

    destroyScope(scope = document) {
      rootsInScope(scope).forEach(root => {
        pendingLazyRoots.get(root)?.disconnect();
        pendingLazyRoots.delete(root);
        root.__productsSliderInlineController?.destroy();
      });
    },
  };

  window.DrhProductsSliderInline = manager;

  const initialize = () => manager.initScope(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  document.addEventListener('shopify:section:load', event => manager.initScope(event.target));
  document.addEventListener('shopify:section:unload', event => manager.destroyScope(event.target));
})();
